import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { Github, ArrowLeft, Filter, CheckCircle, XCircle, Loader2, AlertCircle, Info, Clock, Target, EyeOff, RotateCcw } from 'lucide-react';
import Header from './Header';

const API = '';

const EvidenceDetails = () => {
  const { user, currentWorkspace } = useAuth();
  const navigate = useNavigate();
  const { technology: technologyParam } = useParams();
  // Route param is hyphenated (e.g. "google-workspace") but evidence is
  // stored/queried with underscores ("google_workspace"). Normalize so the
  // API lookups and technology comparisons below match the DB.
  const technology = (technologyParam || '').replace(/-/g, '_');
  const [evidenceHistory, setEvidenceHistory] = useState([]);
  const [allHistory, setAllHistory] = useState([]); // full list of past runs for the workspace
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [runsListOpen, setRunsListOpen] = useState(false);
  const [filteredEvidence, setFilteredEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRepo, setSelectedRepo] = useState(null);
  const [repoModalOpen, setRepoModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    riskRating: 'all',
    complianceStatus: 'all',
    searchQuery: ''
  });
  // Tab: 'active' | 'ignored'
  const [activeTab, setActiveTab] = useState('active');
  // Map of repo_full_name -> ignored entry (reason, who, when)
  const [ignoredMap, setIgnoredMap] = useState({});
  // Pending ignore dialog: {repo, reason}
  const [ignoreDialog, setIgnoreDialog] = useState(null);
  const [ignoreReason, setIgnoreReason] = useState('');
  const [ignoreSaving, setIgnoreSaving] = useState(false);
  // Per-check ignore: { "<repo_full_name>::<check_id>": entry }
  const [ignoredChecksMap, setIgnoredChecksMap] = useState({});
  // Pending check-ignore dialog: { repo, check } | null
  const [ignoreCheckDialog, setIgnoreCheckDialog] = useState(null);
  const [ignoreCheckReason, setIgnoreCheckReason] = useState('');
  const [ignoreCheckSaving, setIgnoreCheckSaving] = useState(false);
  // Org-level ignore (cascades to all repos under that org during the next sync)
  // Map of org_login -> ignored entry (reason, who, when)
  const [ignoredOrgsMap, setIgnoredOrgsMap] = useState({});
  const [orgTab, setOrgTab] = useState('active');
  // Single-org scope. When set, the Repository Evidence list narrows to repos
  // owned by this login (within whichever orgTab is active).
  const [selectedOrgLogin, setSelectedOrgLogin] = useState(null);
  // Pending org-ignore dialog: { login, name } | null
  const [orgIgnoreDialog, setOrgIgnoreDialog] = useState(null);
  const [orgIgnoreReason, setOrgIgnoreReason] = useState('');
  const [orgIgnoreSaving, setOrgIgnoreSaving] = useState(false);

  useEffect(() => {
    if (user && currentWorkspace) {
      loadEvidence();
      loadIgnored();
      loadIgnoredChecks();
      loadIgnoredOrgs();
    }
  }, [user, currentWorkspace, technology]);

  useEffect(() => {
    applyFilters();
  }, [evidenceHistory, filters, ignoredMap, ignoredOrgsMap, activeTab, orgTab, selectedOrgLogin]);

  // Build a stable key for a repo. We prefer "owner/name" then fallback to name.
  const repoKey = (repo) => {
    if (!repo) return '';
    if (repo.full_name) return repo.full_name;
    const owner = repo.owner?.login || repo.github_username || '';
    return owner ? `${owner}/${repo.name}` : repo.name;
  };

  // Stable per-check identifier within a repo: "<category>|<name>"
  const checkId = (check) => {
    if (!check) return '';
    return `${check.category || ''}|${check.name || ''}`;
  };
  // Composite key for ignoredChecksMap
  const ignoredCheckKey = (repoFullName, cId) => `${repoFullName}::${cId}`;

  // Compute live summary for a repo, excluding ignored checks
  const computeSummary = (repo) => {
    const checks = repo?.detailed_checks?.checks || [];
    const repoFn = repoKey(repo);
    let passed = 0;
    let failed = 0;
    let ignored = 0;
    checks.forEach((c) => {
      const k = ignoredCheckKey(repoFn, checkId(c));
      if (ignoredChecksMap[k]) {
        ignored += 1;
        return;
      }
      if (c.status === 'pass') passed += 1;
      else if (c.status === 'fail') failed += 1;
    });
    const total = checks.length;
    const denom = passed + failed;
    const score = denom > 0 ? Math.round((passed / denom) * 100) : 0;
    return { total, passed, failed, ignored, compliance_score: score };
  };

  const loadEvidence = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/api/technologies/evidence-history/${user.id}/${currentWorkspace.id}?technology=${technology}`
      );

      const allEvidence = response.data.history || [];
      setAllHistory(allEvidence);
      // Default to the most recent run
      const mostRecent = allEvidence.length > 0 ? [allEvidence[0]] : [];
      setEvidenceHistory(mostRecent);
      setSelectedRunId(allEvidence[0]?.evidence_id || null);

    } catch (error) {
      console.error('Failed to load evidence:', error);
      toast.error('Failed to load evidence history');
    } finally {
      setLoading(false);
    }
  };

  const selectRun = (evidenceId) => {
    const run = allHistory.find((r) => r.evidence_id === evidenceId);
    if (!run) return;
    setSelectedRunId(evidenceId);
    setEvidenceHistory([run]);
    setRunsListOpen(false);
  };

  const loadIgnored = async () => {
    try {
      const res = await axios.get(
        `${API}/api/technologies/${technology}/ignored-repositories`,
        { params: { workspace_id: currentWorkspace.id } },
      );
      const map = {};
      (res.data?.items || []).forEach((it) => {
        map[it.repo_full_name] = it;
      });
      setIgnoredMap(map);
    } catch (e) {
      console.error('Failed to load ignored repos', e);
    }
  };

  const submitIgnore = async () => {
    if (!ignoreDialog) return;
    const reason = ignoreReason.trim();
    if (!reason) {
      toast.error('Please enter a reason');
      return;
    }
    setIgnoreSaving(true);
    try {
      const res = await axios.post(
        `${API}/api/technologies/${technology}/ignored-repositories`,
        {
          workspace_id: currentWorkspace.id,
          user_id: user.id,
          user_email: user.email,
          repo_full_name: repoKey(ignoreDialog.repo),
          reason,
        },
      );
      const item = res.data?.item;
      if (item) {
        setIgnoredMap((m) => ({ ...m, [item.repo_full_name]: item }));
      }
      toast.success('Repository moved to Ignored');
      setIgnoreDialog(null);
      setIgnoreReason('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to ignore repository');
    } finally {
      setIgnoreSaving(false);
    }
  };

  const handleUnignore = async (repo) => {
    const key = repoKey(repo);
    try {
      await axios.delete(
        `${API}/api/technologies/${technology}/ignored-repositories`,
        { params: { workspace_id: currentWorkspace.id, repo_full_name: key } },
      );
      setIgnoredMap((m) => {
        const copy = { ...m };
        delete copy[key];
        return copy;
      });
      toast.success('Repository moved back to Active');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to un-ignore repository');
    }
  };

  // ---- Organization-level ignore (cascades to repos at collection time) ----

  const loadIgnoredOrgs = async () => {
    try {
      const res = await axios.get(
        `${API}/api/technologies/${technology}/ignored-organizations`,
        { params: { workspace_id: currentWorkspace.id } },
      );
      const map = {};
      (res.data?.items || []).forEach((it) => {
        if (it.org_login) map[it.org_login.toLowerCase()] = it;
      });
      setIgnoredOrgsMap(map);
    } catch (e) {
      console.error('Failed to load ignored orgs', e);
    }
  };

  const submitIgnoreOrg = async () => {
    if (!orgIgnoreDialog) return;
    const reason = orgIgnoreReason.trim();
    if (!reason) {
      toast.error('Please enter a reason');
      return;
    }
    setOrgIgnoreSaving(true);
    try {
      const res = await axios.post(
        `${API}/api/technologies/${technology}/ignored-organizations`,
        {
          workspace_id: currentWorkspace.id,
          user_id: user.id,
          user_email: user.email,
          org_login: orgIgnoreDialog.login,
          reason,
        },
      );
      const item = res.data?.item;
      const eff = res.data?.effects || {};
      if (item && item.org_login) {
        setIgnoredOrgsMap((m) => ({ ...m, [item.org_login.toLowerCase()]: item }));
      }
      const cleared = eff.controls_cleared || 0;
      const jobs = eff.jobs_enqueued || 0;
      const detail =
        cleared || jobs
          ? ` ${cleared} control${cleared === 1 ? '' : 's'} reverted, ${jobs} re-evaluation${jobs === 1 ? '' : 's'} queued.`
          : '';
      toast.success(
        `Organization ignored — its repos will be skipped on the next sync.${detail}`,
      );
      setOrgIgnoreDialog(null);
      setOrgIgnoreReason('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to ignore organization');
    } finally {
      setOrgIgnoreSaving(false);
    }
  };

  const handleUnignoreOrg = async (login) => {
    try {
      await axios.delete(
        `${API}/api/technologies/${technology}/ignored-organizations`,
        { params: { workspace_id: currentWorkspace.id, org_login: login } },
      );
      setIgnoredOrgsMap((m) => {
        const copy = { ...m };
        delete copy[login.toLowerCase()];
        return copy;
      });
      toast.success('Organization moved back to Active');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to un-ignore organization');
    }
  };

  const loadIgnoredChecks = async () => {
    try {
      const res = await axios.get(
        `${API}/api/technologies/${technology}/ignored-repository-checks`,
        { params: { workspace_id: currentWorkspace.id } },
      );
      const map = {};
      (res.data?.items || []).forEach((it) => {
        map[ignoredCheckKey(it.repo_full_name, it.check_id)] = it;
      });
      setIgnoredChecksMap(map);
    } catch (e) {
      console.error('Failed to load ignored checks', e);
    }
  };

  const submitIgnoreCheck = async () => {
    if (!ignoreCheckDialog) return;
    const reason = ignoreCheckReason.trim();
    if (!reason) {
      toast.error('Please enter a reason');
      return;
    }
    setIgnoreCheckSaving(true);
    try {
      const repoFn = repoKey(ignoreCheckDialog.repo);
      const cId = checkId(ignoreCheckDialog.check);
      const res = await axios.post(
        `${API}/api/technologies/${technology}/ignored-repository-checks`,
        {
          workspace_id: currentWorkspace.id,
          user_id: user.id,
          user_email: user.email,
          repo_full_name: repoFn,
          check_id: cId,
          reason,
        },
      );
      const item = res.data?.item;
      if (item) {
        setIgnoredChecksMap((m) => ({
          ...m,
          [ignoredCheckKey(item.repo_full_name, item.check_id)]: item,
        }));
      }
      toast.success('Check marked as Not Applicable');
      setIgnoreCheckDialog(null);
      setIgnoreCheckReason('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to ignore check');
    } finally {
      setIgnoreCheckSaving(false);
    }
  };

  const handleRestoreCheck = async (repo, check) => {
    const repoFn = repoKey(repo);
    const cId = checkId(check);
    try {
      await axios.delete(
        `${API}/api/technologies/${technology}/ignored-repository-checks`,
        {
          params: {
            workspace_id: currentWorkspace.id,
            repo_full_name: repoFn,
            check_id: cId,
          },
        },
      );
      setIgnoredChecksMap((m) => {
        const copy = { ...m };
        delete copy[ignoredCheckKey(repoFn, cId)];
        return copy;
      });
      toast.success('Check restored');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to restore check');
    }
  };

  const applyFilters = () => {
    // Get all repositories from evidence
    let allRepos = [];
    evidenceHistory.forEach(evidence => {
      const repos = evidence.data?.raw_data?.repositoryInformation?.repositories || [];
      repos.forEach(repo => {
        allRepos.push({
          ...repo,
          evidence_id: evidence._id,
          collected_at: evidence.collected_at,
          github_2fa: evidence.github_2fa,
          github_username: evidence.github_username
        });
      });
    });

    // Partition by org tab (Active orgs vs Ignored orgs) FIRST — the org
    // panel is the master control for what's visible below. Then narrow by
    // the per-repo Active/Ignored tab, plus the optional single-org scope.
    allRepos = allRepos.filter((repo) => {
      const ownerLogin = (repo.owner?.login || repo.github_username || '').toLowerCase();
      const ownerOrgIgnored = !!ignoredOrgsMap[ownerLogin];
      // Org tab partition: Active tab shows repos under active orgs;
      // Ignored tab shows repos under ignored orgs.
      if (orgTab === 'active' && ownerOrgIgnored) return false;
      if (orgTab === 'ignored' && !ownerOrgIgnored) return false;
      // Single-org scope (click a row in the Organizations card).
      if (selectedOrgLogin && ownerLogin !== selectedOrgLogin.toLowerCase()) return false;
      // Per-repo Active/Ignored partition (manual repo-ignore).
      const repoIgnored = !!ignoredMap[repoKey(repo)];
      return activeTab === 'ignored' ? repoIgnored : !repoIgnored;
    });

    // Apply filters
    if (filters.searchQuery) {
      allRepos = allRepos.filter(repo =>
        repo.name?.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
        repo.owner?.login?.toLowerCase().includes(filters.searchQuery.toLowerCase())
      );
    }

    if (filters.riskRating !== 'all') {
      allRepos = allRepos.filter(repo => {
        const riskRating = calculateRepoRisk(repo);
        return riskRating.toLowerCase() === filters.riskRating.toLowerCase();
      });
    }

    if (filters.complianceStatus !== 'all') {
      allRepos = allRepos.filter(repo => {
        const status = calculateComplianceStatus(repo);
        return status.toLowerCase() === filters.complianceStatus.toLowerCase();
      });
    }

    setFilteredEvidence(allRepos);
  };

  const calculateRepoRisk = (repo) => {
    const isPrivate = repo.private;
    const has2FA = repo.github_2fa;
    const branchProtection = repo.branch_protection?.enabled;
    const vulnerabilityAlerts = repo.vulnerability_alerts_enabled;

    if (!isPrivate || !has2FA) return 'High';
    if (!branchProtection || !vulnerabilityAlerts) return 'Medium';
    return 'Low';
  };

  const calculateComplianceStatus = (repo) => {
    const risk = calculateRepoRisk(repo);
    if (risk === 'High') return 'Failed';
    if (risk === 'Medium') return 'Partial';
    return 'Passed';
  };

  const getRepoFindings = (repo) => {
    const findings = [];
    const isPrivate = repo.private;
    const has2FA = repo.github_2fa;
    const branchProtection = repo.branch_protection?.enabled;
    const vulnerabilityAlerts = repo.vulnerability_alerts_enabled;

    if (!isPrivate) findings.push('Repository is public');
    if (!branchProtection) findings.push('No branch protection');
    if (!vulnerabilityAlerts) findings.push('Vulnerability alerts disabled');
    if (!has2FA) findings.push('2FA not enabled');

    if (findings.length === 0) {
      findings.push('Repository configured as private');
      if (branchProtection) findings.push('Branch protection enabled');
    }

    return findings;
  };

  const getTechIcon = (tech) => {
    switch (tech) {
      case 'github':
        return <Github className="w-4 h-4 text-slate-700" />;
      default:
        return <Github className="w-4 h-4 text-slate-700" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg">
        <Header />
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-navy-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button
          onClick={() => navigate('/technologies')}
          variant="outline"
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Technologies
        </Button>

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
              {getTechIcon(technology)}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">{(technology || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())} Evidence Details</h1>
              <p className="text-slate-600">
                {evidenceHistory.length > 0
                  ? `Collection run on ${new Date(evidenceHistory[0].collected_at).toLocaleString()}${evidenceHistory[0].collected_by_email ? ` · by ${evidenceHistory[0].collected_by_email}` : ''}`
                  : 'Most recent compliance evidence collection'}
              </p>
            </div>
          </div>
        </div>

        {/* Previous runs selector — workspace-visible run history */}
        {allHistory.length > 1 && (
          <Card className="mb-4" data-testid="previous-runs-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="text-sm">
                  <span className="text-slate-500">Viewing run: </span>
                  <span className="font-medium text-slate-900">
                    {new Date(evidenceHistory[0]?.collected_at).toLocaleString()}
                  </span>
                  {evidenceHistory[0]?.collected_by_email && (
                    <span className="text-slate-500"> · by {evidenceHistory[0].collected_by_email}</span>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRunsListOpen((v) => !v)}
                  data-testid="toggle-previous-runs-btn"
                >
                  {runsListOpen ? 'Hide' : `Previous runs (${allHistory.length - 1})`}
                </Button>
              </div>
              {runsListOpen && (
                <div className="mt-3 border-t border-slate-100 pt-3">
                  <table className="min-w-full text-sm" data-testid="previous-runs-table">
                    <thead>
                      <tr className="text-[11px] uppercase tracking-wide text-slate-500">
                        <th className="text-left px-3 py-1.5 font-medium">When</th>
                        <th className="text-left px-3 py-1.5 font-medium">Run by</th>
                        <th className="text-left px-3 py-1.5 font-medium">Summary</th>
                        <th className="text-right px-3 py-1.5 font-medium">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allHistory.map((run) => {
                        const isSelected = run.evidence_id === selectedRunId;
                        const summary = run.summary || {};
                        const repoCount = summary.repositories_scanned ?? summary.total_data_points ?? run.total_repositories ?? 0;
                        const userCount = summary.total_users ?? 0;
                        const summaryText = technology === 'google_workspace'
                          ? `${userCount} users · ${summary.admins ?? 0} admins · ${summary.suspended_users ?? 0} suspended`
                          : `${repoCount} repositories · risk ${run.risk_rating || summary.risk_assessment || '—'}`;
                        return (
                          <tr key={run.evidence_id} className={`border-t border-slate-100 ${isSelected ? 'bg-slate-50' : 'hover:bg-slate-50'}`}>
                            <td className="px-3 py-2 text-slate-700">{new Date(run.collected_at).toLocaleString()}</td>
                            <td className="px-3 py-2 text-slate-600 text-xs">{run.collected_by_email || <span className="text-slate-400">unknown</span>}</td>
                            <td className="px-3 py-2 text-slate-500 text-xs">{summaryText}</td>
                            <td className="px-3 py-2 text-right">
                              {isSelected ? (
                                <span className="text-xs text-emerald-700 font-medium">Current</span>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2 text-xs"
                                  onClick={() => selectRun(run.evidence_id)}
                                  data-testid={`select-run-${run.evidence_id}`}
                                >
                                  View
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {evidenceHistory.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Evidence Found</h3>
              <p className="text-slate-600 mb-4">
                No evidence has been collected for this technology yet.
              </p>
              <Button onClick={() => navigate('/technologies')} className="bg-navy-600 hover:bg-navy-700">
                Go to Technologies
              </Button>
            </CardContent>
          </Card>
        ) : technology === 'google_workspace' ? (
          <GoogleWorkspaceEvidence evidenceHistory={evidenceHistory} />
        ) : (
          <>
            {/* Filters */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Filter className="w-5 h-5" />
                  <span>Filters</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="search">Search Repositories</Label>
                    <Input
                      id="search"
                      placeholder="Search by name or owner..."
                      value={filters.searchQuery}
                      onChange={(e) => setFilters({ ...filters, searchQuery: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="risk">Risk Rating</Label>
                    <select
                      id="risk"
                      value={filters.riskRating}
                      onChange={(e) => setFilters({ ...filters, riskRating: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500"
                    >
                      <option value="all">All Risks</option>
                      <option value="low">Low Risk</option>
                      <option value="medium">Medium Risk</option>
                      <option value="high">High Risk</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="compliance">Compliance Status</Label>
                    <select
                      id="compliance"
                      value={filters.complianceStatus}
                      onChange={(e) => setFilters({ ...filters, complianceStatus: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500"
                    >
                      <option value="all">All Statuses</option>
                      <option value="passed">Passed</option>
                      <option value="partial">Partial</option>
                      <option value="failed">Failed</option>
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Organizations — workspace can ignore an entire org to skip it
                (and all its repos) on the next sync. */}
            {(() => {
              // Aggregate org list from the selected run(s).
              const orgMap = new Map();
              evidenceHistory.forEach((ev) => {
                (ev.organizations || []).forEach((o) => {
                  if (o?.login) orgMap.set(o.login.toLowerCase(), o);
                });
                // Backfill from previously-ignored orgs that no longer appear
                // in this run (so the auditor can still un-ignore them).
              });
              Object.values(ignoredOrgsMap).forEach((it) => {
                const k = (it.org_login || '').toLowerCase();
                if (k && !orgMap.has(k)) {
                  orgMap.set(k, { login: it.org_login, name: it.org_login, two_factor_requirement_enabled: false });
                }
              });
              const allOrgs = Array.from(orgMap.values());
              if (allOrgs.length === 0) return null;

              const activeOrgs = allOrgs.filter((o) => !ignoredOrgsMap[o.login.toLowerCase()]);
              const ignoredOrgs = allOrgs.filter((o) => ignoredOrgsMap[o.login.toLowerCase()]);
              const rows = orgTab === 'ignored' ? ignoredOrgs : activeOrgs;

              return (
                <Card className="mb-4" data-testid="organizations-card">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span>Organizations</span>
                      <Badge className="bg-purple-100 text-purple-700">
                        {allOrgs.length} total · {ignoredOrgs.length} ignored
                      </Badge>
                    </CardTitle>
                    <CardDescription>
                      Ignoring an organization skips it — and every repo it owns —
                      on the next GitHub sync.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Tabs value={orgTab} onValueChange={(v) => { setOrgTab(v); setSelectedOrgLogin(null); }} className="mb-3">
                      <TabsList>
                        <TabsTrigger value="active" data-testid="org-tab-active">
                          Active
                          <Badge className="ml-2 bg-blue-100 text-blue-700">
                            {activeOrgs.length}
                          </Badge>
                        </TabsTrigger>
                        <TabsTrigger value="ignored" data-testid="org-tab-ignored">
                          Ignored
                          <Badge className="ml-2 bg-slate-200 text-slate-700">
                            {ignoredOrgs.length}
                          </Badge>
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                    {rows.length === 0 ? (
                      <p className="text-sm text-slate-500 py-4 text-center">
                        {orgTab === 'ignored'
                          ? 'No ignored organizations.'
                          : 'No organizations to display.'}
                      </p>
                    ) : (
                      <div className="divide-y divide-slate-100 border border-slate-100 rounded-lg">
                        {rows.map((org) => {
                          const key = org.login.toLowerCase();
                          const isIgnored = !!ignoredOrgsMap[key];
                          const entry = ignoredOrgsMap[key];
                          const isScoped = selectedOrgLogin && selectedOrgLogin.toLowerCase() === key;
                          return (
                            <div
                              key={key}
                              role="button"
                              tabIndex={0}
                              onClick={() => setSelectedOrgLogin(isScoped ? null : org.login)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setSelectedOrgLogin(isScoped ? null : org.login);
                                }
                              }}
                              className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors ${
                                isScoped ? 'bg-blue-50' : 'hover:bg-slate-50'
                              }`}
                              data-testid={`org-row-${org.login}`}
                            >
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-slate-900 truncate">
                                  {org.name || org.login}
                                  <span className="ml-2 text-xs font-normal text-slate-500">
                                    @{org.login}
                                  </span>
                                  {isScoped && (
                                    <Badge className="ml-2 bg-blue-100 text-blue-700 text-[10px] uppercase tracking-wide">
                                      Scoped
                                    </Badge>
                                  )}
                                </p>
                                <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                                  {org.two_factor_requirement_enabled ? (
                                    <span className="inline-flex items-center text-emerald-700">
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                      2FA enforced
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center text-amber-700">
                                      <AlertCircle className="w-3 h-3 mr-1" />
                                      2FA not enforced
                                    </span>
                                  )}
                                  {isIgnored && entry?.reason && (
                                    <span className="text-slate-400 italic truncate max-w-[320px]">
                                      · {entry.reason}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {isIgnored ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                                  onClick={(e) => { e.stopPropagation(); handleUnignoreOrg(org.login); }}
                                  data-testid={`org-restore-${org.login}`}
                                >
                                  <RotateCcw className="w-3.5 h-3.5 mr-1" />
                                  Restore
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-slate-700 border-slate-300 hover:bg-slate-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOrgIgnoreDialog(org);
                                    setOrgIgnoreReason('');
                                  }}
                                  data-testid={`org-ignore-${org.login}`}
                                >
                                  <EyeOff className="w-3.5 h-3.5 mr-1" />
                                  Ignore
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })()}

            {/* Tabs: Active / Ignored */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-4">
              <TabsList>
                <TabsTrigger value="active" data-testid="repo-tab-active">
                  Active
                  <Badge className="ml-2 bg-blue-100 text-blue-700">
                    {(() => {
                      let total = 0;
                      evidenceHistory.forEach((ev) => {
                        const repos = ev.data?.raw_data?.repositoryInformation?.repositories || [];
                        repos.forEach((r) => {
                          const ownerLogin = (r.owner?.login || '').toLowerCase();
                          if (ignoredOrgsMap[ownerLogin]) return;
                          if (!ignoredMap[repoKey(r)]) total += 1;
                        });
                      });
                      return total;
                    })()}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="ignored" data-testid="repo-tab-ignored">
                  Ignored
                  <Badge className="ml-2 bg-slate-200 text-slate-700">
                    {Object.keys(ignoredMap).length}
                  </Badge>
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Evidence Table */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span>Repository Evidence</span>
                    <Badge
                      className={
                        orgTab === 'ignored'
                          ? 'bg-slate-200 text-slate-700'
                          : 'bg-purple-100 text-purple-700'
                      }
                      data-testid="repos-org-scope-badge"
                    >
                      {orgTab === 'ignored' ? 'Ignored orgs' : 'Active orgs'}
                    </Badge>
                    {selectedOrgLogin && (
                      <Badge className="bg-blue-50 text-blue-800 border border-blue-200 flex items-center gap-1.5 px-2 py-1">
                        <span>@{selectedOrgLogin}</span>
                        <button
                          type="button"
                          onClick={() => setSelectedOrgLogin(null)}
                          className="rounded-full hover:bg-blue-100 px-1.5 -mr-1 text-blue-700"
                          aria-label="Clear org scope"
                          data-testid="clear-org-scope-btn"
                        >
                          ×
                        </button>
                      </Badge>
                    )}
                  </div>
                  <Badge className={activeTab === 'ignored' ? 'bg-slate-200 text-slate-700' : 'bg-blue-100 text-blue-700'}>
                    {filteredEvidence.length} {activeTab === 'ignored' ? 'Ignored' : ''} Repositories
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Collected on {(() => {
                    const date = new Date(evidenceHistory[0].collected_at);
                    const day = String(date.getDate()).padStart(2, '0');
                    const month = date.toLocaleString('en-US', { month: 'short' });
                    const year = date.getFullYear();
                    const hours = String(date.getHours()).padStart(2, '0');
                    const minutes = String(date.getMinutes()).padStart(2, '0');
                    const seconds = String(date.getSeconds()).padStart(2, '0');
                    return `${day} ${month} ${year} ${hours}:${minutes}:${seconds}`;
                  })()}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-slate-100 border-b-2 border-slate-200">
                        <th className="text-left p-3 text-sm font-semibold text-slate-700">Repository</th>
                        <th className="text-left p-3 text-sm font-semibold text-slate-700">Owner</th>
                        <th className="text-left p-3 text-sm font-semibold text-slate-700">Security Details</th>
                        <th className="text-left p-3 text-sm font-semibold text-slate-700">Risk</th>
                        <th className="text-left p-3 text-sm font-semibold text-slate-700">Compliance</th>
                        <th className="text-left p-3 text-sm font-semibold text-slate-700">Findings</th>
                        <th className="text-right p-3 text-sm font-semibold text-slate-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEvidence.map((repo, index) => {
                        const riskRating = calculateRepoRisk(repo);
                        const complianceStatus = calculateComplianceStatus(repo);
                        const findings = getRepoFindings(repo);
                        const isPrivate = repo.private;
                        const has2FA = repo.github_2fa;
                        const branchProtection = repo.branch_protection?.enabled;
                        const vulnerabilityAlerts = repo.vulnerability_alerts_enabled;

                        return (
                          <tr 
                            key={index}
                            className={`border-b border-slate-200 hover:bg-slate-50 transition-colors ${
                              index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                            }`}
                          >
                            {/* Repository */}
                            <td className="p-3">
                              <div>
                                <button
                                  onClick={() => {
                                    setSelectedRepo(repo);
                                    setRepoModalOpen(true);
                                  }}
                                  className="font-medium text-slate-900 hover:text-navy-600 hover:underline cursor-pointer text-left"
                                >
                                  {repo.name}
                                </button>
                                {repo.html_url && (
                                  <a 
                                    href={repo.html_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-600 hover:underline block mt-1"
                                  >
                                    View on GitHub
                                  </a>
                                )}
                                <p className="text-xs text-slate-600 mt-1">
                                  {isPrivate ? (
                                    <Badge className="bg-slate-600 text-white text-xs">Private</Badge>
                                  ) : (
                                    <Badge className="bg-blue-100 text-blue-700 text-xs">Public</Badge>
                                  )}
                                </p>
                              </div>
                            </td>

                            {/* Owner */}
                            <td className="p-3">
                              <p className="font-medium text-slate-900">
                                {repo.owner?.login || repo.github_username || 'N/A'}
                              </p>
                            </td>

                            {/* Security Details */}
                            <td className="p-3">
                              <div className="space-y-1">
                                <div className="flex items-center space-x-1">
                                  {has2FA ? (
                                    <>
                                      <CheckCircle className="w-4 h-4 text-navy-600" />
                                      <span className="text-xs text-slate-700">2FA</span>
                                    </>
                                  ) : (
                                    <>
                                      <XCircle className="w-4 h-4 text-red-600" />
                                      <span className="text-xs text-slate-700">2FA</span>
                                    </>
                                  )}
                                </div>
                                <div className="flex items-center space-x-1">
                                  {branchProtection ? (
                                    <>
                                      <CheckCircle className="w-4 h-4 text-navy-600" />
                                      <span className="text-xs text-slate-700">Branch Prot</span>
                                    </>
                                  ) : (
                                    <>
                                      <XCircle className="w-4 h-4 text-red-600" />
                                      <span className="text-xs text-slate-700">Branch Prot</span>
                                    </>
                                  )}
                                </div>
                                <div className="flex items-center space-x-1">
                                  {vulnerabilityAlerts ? (
                                    <>
                                      <CheckCircle className="w-4 h-4 text-navy-600" />
                                      <span className="text-xs text-slate-700">Vuln Alerts</span>
                                    </>
                                  ) : (
                                    <>
                                      <XCircle className="w-4 h-4 text-red-600" />
                                      <span className="text-xs text-slate-700">Vuln Alerts</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </td>

                            {/* Risk */}
                            <td className="p-3">
                              <Badge className={`
                                ${riskRating === 'Low' ? 'bg-emerald-100 text-navy-700' : ''}
                                ${riskRating === 'Medium' ? 'bg-yellow-100 text-yellow-700' : ''}
                                ${riskRating === 'High' ? 'bg-red-100 text-red-700' : ''}
                              `}>
                                {riskRating}
                              </Badge>
                            </td>

                            {/* Compliance */}
                            <td className="p-3">
                              <Badge className={`
                                ${complianceStatus === 'Passed' ? 'bg-emerald-100 text-navy-700' : ''}
                                ${complianceStatus === 'Partial' ? 'bg-yellow-100 text-yellow-700' : ''}
                                ${complianceStatus === 'Failed' ? 'bg-red-100 text-red-700' : ''}
                              `}>
                                {complianceStatus}
                              </Badge>
                              {repo.detailed_checks?.summary && (() => {
                                const live = computeSummary(repo);
                                return (
                                <p className="text-[11px] mt-1.5 flex items-center gap-1.5 flex-wrap" data-testid={`repo-checks-${repoKey(repo)}`}>
                                  <span className="inline-flex items-center text-emerald-700">
                                    <CheckCircle className="w-3 h-3 mr-0.5" />
                                    {live.passed} passed
                                  </span>
                                  <span className="text-slate-300">·</span>
                                  <span className="inline-flex items-center text-red-700">
                                    <XCircle className="w-3 h-3 mr-0.5" />
                                    {live.failed} failed
                                  </span>
                                  {live.ignored > 0 && (
                                    <>
                                      <span className="text-slate-300">·</span>
                                      <span className="inline-flex items-center text-slate-600">
                                        <EyeOff className="w-3 h-3 mr-0.5" />
                                        {live.ignored} N/A
                                      </span>
                                    </>
                                  )}
                                </p>
                                );
                              })()}
                              <p className="text-xs text-slate-600 mt-1">SOC2/GDPR/ISO</p>
                            </td>

                            {/* Findings */}
                            <td className="p-3">
                              <ul className="space-y-1">
                                {findings.slice(0, 3).map((finding, idx) => (
                                  <li key={idx} className="text-xs text-slate-600 flex items-start">
                                    <span className="mr-1">•</span>
                                    <span>{finding}</span>
                                  </li>
                                ))}
                                {findings.length > 3 && (
                                  <li className="text-xs text-slate-500">+{findings.length - 3} more</li>
                                )}
                              </ul>
                            </td>

                            {/* Actions */}
                            <td className="p-3 text-right">
                              {activeTab === 'ignored' ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleUnignore(repo)}
                                  className="text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                                  data-testid={`repo-unignore-btn-${repoKey(repo)}`}
                                >
                                  <RotateCcw className="w-3 h-3 mr-1" />
                                  Un-ignore
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setIgnoreReason('');
                                    setIgnoreDialog({ repo });
                                  }}
                                  className="text-xs text-slate-500 hover:text-slate-700"
                                  data-testid={`repo-ignore-btn-${repoKey(repo)}`}
                                >
                                  <EyeOff className="w-3 h-3 mr-1" />
                                  Ignore
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {filteredEvidence.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-slate-600">No repositories match your filters</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Ignore Check (mark as Not Applicable) Dialog */}
      <Dialog
        open={!!ignoreCheckDialog}
        onOpenChange={(open) => {
          if (!open && !ignoreCheckSaving) {
            setIgnoreCheckDialog(null);
            setIgnoreCheckReason('');
          }
        }}
      >
        <DialogContent className="sm:max-w-md" data-testid="check-ignore-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <EyeOff className="w-5 h-5 text-slate-500" />
              <span>Mark Check as Not Applicable</span>
            </DialogTitle>
            <DialogDescription>
              {ignoreCheckDialog?.check ? (
                <>
                  This will exclude{' '}
                  <span className="font-medium text-slate-800">
                    "{ignoreCheckDialog.check.name}"
                  </span>{' '}
                  from this repo's compliance score and Pass/Fail counts.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            <Label className="text-sm font-medium text-slate-700">
              Reason <span className="text-red-500">*</span>
            </Label>
            <Textarea
              value={ignoreCheckReason}
              onChange={(e) => setIgnoreCheckReason(e.target.value)}
              placeholder="e.g. 'Org enforces 2FA at SSO; per-repo not needed', 'Branch protection enforced via org policy'"
              rows={3}
              className="resize-none mt-2"
              autoFocus
              data-testid="check-ignore-reason-input"
            />
            <p className="text-xs text-slate-500 mt-2">
              Saved with your name and timestamp for the audit trail.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIgnoreCheckDialog(null);
                setIgnoreCheckReason('');
              }}
              disabled={ignoreCheckSaving}
              data-testid="check-ignore-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={submitIgnoreCheck}
              disabled={!ignoreCheckReason.trim() || ignoreCheckSaving}
              className="bg-slate-700 hover:bg-slate-800 text-white"
              data-testid="check-ignore-submit-btn"
            >
              {ignoreCheckSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…
                </>
              ) : (
                <>
                  <EyeOff className="w-4 h-4 mr-2" /> Mark Not Applicable
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ignore Repository Dialog */}
      <Dialog
        open={!!ignoreDialog}
        onOpenChange={(open) => {
          if (!open && !ignoreSaving) {
            setIgnoreDialog(null);
            setIgnoreReason('');
          }
        }}
      >
        <DialogContent className="sm:max-w-md" data-testid="repo-ignore-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <EyeOff className="w-5 h-5 text-slate-500" />
              <span>Ignore Repository</span>
            </DialogTitle>
            <DialogDescription>
              {ignoreDialog?.repo ? (
                <>
                  <span className="font-medium text-slate-800">
                    {repoKey(ignoreDialog.repo)}
                  </span>{' '}
                  will be moved to the Ignored tab and excluded from your active
                  compliance review. You can un-ignore it anytime.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            <Label className="text-sm font-medium text-slate-700">
              Reason <span className="text-red-500">*</span>
            </Label>
            <Textarea
              value={ignoreReason}
              onChange={(e) => setIgnoreReason(e.target.value)}
              placeholder="e.g. 'Archived/legacy fork — read-only', 'Personal sandbox', 'Out of scope for SOC2'"
              rows={3}
              className="resize-none mt-2"
              autoFocus
              data-testid="repo-ignore-reason-input"
            />
            <p className="text-xs text-slate-500 mt-2">
              Saved with your name and timestamp for the audit trail.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIgnoreDialog(null);
                setIgnoreReason('');
              }}
              disabled={ignoreSaving}
              data-testid="repo-ignore-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={submitIgnore}
              disabled={!ignoreReason.trim() || ignoreSaving}
              className="bg-slate-700 hover:bg-slate-800 text-white"
              data-testid="repo-ignore-submit-btn"
            >
              {ignoreSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…
                </>
              ) : (
                <>
                  <EyeOff className="w-4 h-4 mr-2" /> Move to Ignored
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ignore Organization Dialog */}
      <Dialog
        open={!!orgIgnoreDialog}
        onOpenChange={(open) => {
          if (!open && !orgIgnoreSaving) {
            setOrgIgnoreDialog(null);
            setOrgIgnoreReason('');
          }
        }}
      >
        <DialogContent className="sm:max-w-md" data-testid="org-ignore-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <EyeOff className="w-5 h-5 text-slate-500" />
              <span>Ignore Organization</span>
            </DialogTitle>
            <DialogDescription>
              {orgIgnoreDialog ? (
                <>
                  <span className="font-medium text-slate-800">
                    @{orgIgnoreDialog.login}
                  </span>{' '}
                  and <strong>every repository it owns</strong> will be skipped on
                  the next GitHub sync — no API calls, no compliance scoring,
                  no findings. You can restore it anytime.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            <Label className="text-sm font-medium text-slate-700">
              Reason <span className="text-red-500">*</span>
            </Label>
            <Textarea
              value={orgIgnoreReason}
              onChange={(e) => setOrgIgnoreReason(e.target.value)}
              placeholder="e.g. 'Third-party contractor org', 'Sandbox / training org', 'Out of audit scope'"
              rows={3}
              className="resize-none mt-2"
              autoFocus
              data-testid="org-ignore-reason-input"
            />
            <p className="text-xs text-slate-500 mt-2">
              Saved with your name and timestamp for the audit trail.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setOrgIgnoreDialog(null);
                setOrgIgnoreReason('');
              }}
              disabled={orgIgnoreSaving}
              data-testid="org-ignore-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={submitIgnoreOrg}
              disabled={!orgIgnoreReason.trim() || orgIgnoreSaving}
              className="bg-slate-700 hover:bg-slate-800 text-white"
              data-testid="org-ignore-submit-btn"
            >
              {orgIgnoreSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…
                </>
              ) : (
                <>
                  <EyeOff className="w-4 h-4 mr-2" /> Move to Ignored
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Repository Details Modal */}
      <Dialog open={repoModalOpen} onOpenChange={setRepoModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Github className="w-6 h-6 text-navy-700" />
              </div>
              <div>
                <h3 className="text-xl font-bold">{selectedRepo?.name}</h3>
                <p className="text-sm text-slate-600 font-normal">
                  Detailed Compliance Checks & Recommendations
                </p>
              </div>
            </DialogTitle>
          </DialogHeader>

          {selectedRepo && selectedRepo.detailed_checks && (() => {
            const liveSummary = computeSummary(selectedRepo);
            return (
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-5 gap-3">
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <p className="text-sm text-slate-600 mb-1">Total Checks</p>
                  <p className="text-3xl font-bold text-blue-700" data-testid="stat-total">
                    {liveSummary.total}
                  </p>
                </div>
                <div className="bg-emerald-50 p-4 rounded-lg text-center">
                  <p className="text-sm text-slate-600 mb-1">Passed</p>
                  <p className="text-3xl font-bold text-navy-700" data-testid="stat-passed">
                    {liveSummary.passed}
                  </p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg text-center">
                  <p className="text-sm text-slate-600 mb-1">Failed</p>
                  <p className="text-3xl font-bold text-red-700" data-testid="stat-failed">
                    {liveSummary.failed}
                  </p>
                </div>
                <div className="bg-slate-100 p-4 rounded-lg text-center">
                  <p className="text-sm text-slate-600 mb-1">Ignored</p>
                  <p className="text-3xl font-bold text-slate-700" data-testid="stat-ignored">
                    {liveSummary.ignored}
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg text-center">
                  <p className="text-sm text-slate-600 mb-1">Compliance Score</p>
                  <p className="text-3xl font-bold text-purple-700" data-testid="stat-score">
                    {liveSummary.compliance_score}%
                  </p>
                </div>
              </div>

              {/* Checks by Category */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-slate-900">Compliance Checks</h4>
                
                {Object.entries(
                  selectedRepo.detailed_checks.checks.reduce((acc, check) => {
                    if (!acc[check.category]) acc[check.category] = [];
                    acc[check.category].push(check);
                    return acc;
                  }, {})
                ).map(([category, checks]) => (
                  <div key={category} className="border rounded-lg p-4">
                    <h5 className="font-semibold text-slate-800 mb-3">{category}</h5>
                    <div className="space-y-3">
                      {checks.map((check, idx) => {
                        const cId = checkId(check);
                        const repoFn = repoKey(selectedRepo);
                        const ignoredEntry = ignoredChecksMap[ignoredCheckKey(repoFn, cId)];
                        const isIgnored = !!ignoredEntry;
                        return (
                        <div
                          key={idx}
                          className={`p-3 rounded-lg border-l-4 ${
                            isIgnored
                              ? 'bg-slate-100 border-slate-400 opacity-75'
                              : check.status === 'pass'
                              ? 'bg-emerald-50 border-navy-500'
                              : check.status === 'fail'
                              ? 'bg-red-50 border-red-500'
                              : 'bg-yellow-50 border-yellow-500'
                          }`}
                          data-testid={`check-row-${cId}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                {isIgnored ? (
                                  <EyeOff className="w-5 h-5 text-slate-500" />
                                ) : check.status === 'pass' ? (
                                  <CheckCircle className="w-5 h-5 text-navy-600" />
                                ) : check.status === 'fail' ? (
                                  <XCircle className="w-5 h-5 text-red-600" />
                                ) : (
                                  <AlertCircle className="w-5 h-5 text-yellow-600" />
                                )}
                                <span className={`font-medium ${isIgnored ? 'text-slate-500 line-through' : 'text-slate-900'}`}>{check.name}</span>
                                {isIgnored && (
                                  <Badge className="ml-1 bg-slate-200 text-slate-700 border-slate-300 text-[10px]">
                                    Not applicable
                                  </Badge>
                                )}
                              </div>
                              <p className={`text-sm mb-2 ${isIgnored ? 'text-slate-500' : 'text-slate-600'}`}>{check.description}</p>
                              <div className="flex items-center space-x-4 text-xs flex-wrap gap-y-1">
                                <span className="text-slate-700">
                                  <strong>Current:</strong> {check.value}
                                </span>
                                {check.priority !== 'info' && (
                                  <Badge
                                    className={`${
                                      check.priority === 'critical'
                                        ? 'bg-red-100 text-red-700'
                                        : check.priority === 'high'
                                        ? 'bg-orange-100 text-orange-700'
                                        : check.priority === 'medium'
                                        ? 'bg-yellow-100 text-yellow-700'
                                        : 'bg-blue-100 text-blue-700'
                                    }`}
                                  >
                                    {check.priority.toUpperCase()}
                                  </Badge>
                                )}
                                <span className="text-slate-500">
                                  Impact: {check.impact.join(', ')}
                                </span>
                              </div>
                              {isIgnored && ignoredEntry?.reason && (
                                <div className="mt-2 px-3 py-2 rounded-md bg-white/70 border border-slate-200 text-xs text-slate-600 whitespace-pre-wrap">
                                  <span className="font-semibold text-slate-700">Reason:</span>{' '}
                                  {ignoredEntry.reason}
                                  {ignoredEntry.ignored_by_email && (
                                    <span className="block mt-1 text-[11px] text-slate-500">
                                      — {ignoredEntry.ignored_by_email}
                                      {ignoredEntry.created_at && (
                                        <> · {new Date(ignoredEntry.created_at).toLocaleString()}</>
                                      )}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="flex-shrink-0">
                              {isIgnored ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleRestoreCheck(selectedRepo, check)}
                                  className="text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                                  data-testid={`check-restore-btn-${cId}`}
                                >
                                  <RotateCcw className="w-3 h-3 mr-1" />
                                  Restore
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setIgnoreCheckReason('');
                                    setIgnoreCheckDialog({ repo: selectedRepo, check });
                                  }}
                                  className="text-xs text-slate-500 hover:text-slate-700"
                                  data-testid={`check-ignore-btn-${cId}`}
                                >
                                  <EyeOff className="w-3 h-3 mr-1" />
                                  Mark N/A
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* AI Recommendations */}
              {selectedRepo.ai_recommendations && selectedRepo.ai_recommendations.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Target className="w-5 h-5 text-purple-600" />
                    <h4 className="text-lg font-semibold text-slate-900">
                      AI-Powered Remediation Recommendations
                    </h4>
                  </div>
                  
                  <div className="space-y-4">
                    {selectedRepo.ai_recommendations.map((recommendation, idx) => (
                      <div key={idx} className="border border-purple-200 rounded-lg p-4 bg-purple-50">
                        <div className="flex items-start justify-between mb-3">
                          <h5 className="font-semibold text-slate-900">{recommendation.check_name}</h5>
                          <div className="flex items-center space-x-2">
                            <Badge
                              className={`${
                                recommendation.priority === 'critical'
                                  ? 'bg-red-100 text-red-700'
                                  : recommendation.priority === 'high'
                                  ? 'bg-orange-100 text-orange-700'
                                  : recommendation.priority === 'medium'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}
                            >
                              {recommendation.priority.toUpperCase()}
                            </Badge>
                            <div className="flex items-center space-x-1 text-xs text-slate-600">
                              <Clock className="w-3 h-3" />
                              <span>{recommendation.estimated_time_minutes} min</span>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div>
                            <p className="text-sm font-medium text-slate-700 mb-2">Remediation Steps:</p>
                            <ol className="list-decimal list-inside space-y-1">
                              {recommendation.remediation_steps.map((step, stepIdx) => (
                                <li key={stepIdx} className="text-sm text-slate-600 ml-2">
                                  {step}
                                </li>
                              ))}
                            </ol>
                          </div>

                          <div className="bg-white p-3 rounded border border-purple-200">
                            <p className="text-xs font-medium text-slate-700 mb-1">Compliance Impact:</p>
                            <p className="text-xs text-slate-600">{recommendation.compliance_impact}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No Recommendations Message */}
              {selectedRepo.ai_recommendations && selectedRepo.ai_recommendations.length === 0 && 
               liveSummary.failed === 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
                  <CheckCircle className="w-12 h-12 text-navy-600 mx-auto mb-2" />
                  <p className="text-emerald-800 font-semibold">All Checks Passed!</p>
                  <p className="text-navy-600 text-sm">This repository meets all compliance requirements.</p>
                </div>
              )}
            </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

/**
 * Read-only Google Workspace Directory evidence view (v1).
 * Shows users, groups, group members, organizational units, admin roles,
 * role assignments, customer info, and verified domains — strictly the
 * data returned by the Google Admin SDK Directory readonly endpoints.
 */
const GoogleWorkspaceEvidence = ({ evidenceHistory }) => {
  const [tab, setTab] = useState('users');
  // Quick-filter for the Users tab driven by the summary strip.
  // null = no filter. Only one filter at a time (kept simple by design).
  // Values: 'suspended' | 'no_2sv' | 'admins' | 'enforced_2sv'
  const [userFilter, setUserFilter] = useState(null);
  const evidence = evidenceHistory[0];
  const di = evidence?.data?.raw_data?.directoryInformation || {};
  const summary = evidence?.summary || {};
  const meta = evidence?.metadata || {};

  const users = di.users || [];
  const groups = di.groups || [];
  const groupMembers = di.group_members || {};
  const orgUnits = di.org_units || [];
  const roles = di.roles || [];
  const roleAssignments = di.role_assignments || [];
  const customer = di.customer || {};
  const domains = di.domains || [];

  // Apply the active quick-filter to the users list.
  const filteredUsers = users.filter((u) => {
    if (!userFilter) return true;
    if (userFilter === 'suspended') return !!u.suspended;
    if (userFilter === 'no_2sv') return !u.isEnrolledIn2Sv && !u.isEnforcedIn2Sv;
    if (userFilter === 'admins') return !!(u.isAdmin || u.isDelegatedAdmin);
    if (userFilter === 'enforced_2sv') return !!u.isEnforcedIn2Sv;
    return true;
  });

  const filterLabels = {
    suspended: 'Suspended users',
    no_2sv: '2-Step Verification not enabled',
    admins: 'Admin users',
    enforced_2sv: '2-Step Verification enforced',
  };

  // Click a summary stat → jump to Users tab + apply filter.
  const applyFilter = (key) => {
    setUserFilter((prev) => (prev === key ? null : key));
    setTab('users');
  };

  const fmt = (s) => {
    if (!s) return '—';
    try {
      return new Date(s).toLocaleString();
    } catch (_) {
      return s;
    }
  };

  const displayName = (u) => u?.name?.fullName || u?.primaryEmail || '—';

  const primaryDomain = customer.customerDomain || summary.customer_domain || '—';
  const adminContact = customer.alternateEmail || summary.primary_admin_email || '—';

  return (
    <>
      {/* Mode banner if mock */}
      {meta.mode === 'mock' && (
        <div className="mb-4 px-3 py-2 bg-amber-50 border border-amber-200 text-amber-800 rounded text-xs">
          ⓘ This evidence was collected in <strong>mock mode</strong>. Set <code>GOOGLE_WS_SERVICE_ACCOUNT_JSON</code> on the server to switch to live data.
        </div>
      )}

      {/* Workspace summary — split into two rows: compliance signals
          first (clickable filters), structural inventory second. */}
      <Card className="mb-6" data-testid="gw-summary">
        <CardContent className="p-5 space-y-5">
          {/* Workspace identity */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 pb-4 border-b border-slate-100">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wide text-slate-500">Workspace</p>
              <p className="text-base font-semibold text-slate-900 truncate" data-testid="gw-primary-domain">{primaryDomain}</p>
              <p className="text-xs text-slate-500 mt-0.5 truncate">Admin: {adminContact}</p>
            </div>
            <p className="text-[11px] text-slate-400 italic">
              Tip: click a metric to filter the Users tab
            </p>
          </div>

          {/* Row 1 — Compliance signals (clickable drill-downs) */}
          <div>
            <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-3 font-medium">Compliance signals</p>
            <div className="flex flex-wrap items-start gap-x-8 gap-y-4">
              <GwStat n={summary.total_users ?? users.length} l="Users" />
              <GwStat n={summary.active_users ?? 0} l="Active" tone="emerald" />
              <GwStat
                n={summary.suspended_users ?? 0}
                l="Suspended"
                tone="red"
                onClick={() => applyFilter('suspended')}
                active={userFilter === 'suspended'}
                testId="gw-stat-suspended"
              />
              <GwStat
                n={summary.admins ?? 0}
                l="Admins"
                onClick={() => applyFilter('admins')}
                active={userFilter === 'admins'}
                testId="gw-stat-admins"
              />
              <GwStat
                n={users.length - (summary.users_with_2sv_enrolled ?? 0)}
                l="Not using 2-Step Verification"
                tone={(users.length - (summary.users_with_2sv_enrolled ?? 0)) > 0 ? 'red' : 'slate'}
                onClick={() => applyFilter('no_2sv')}
                active={userFilter === 'no_2sv'}
                testId="gw-stat-no2sv"
              />
              <GwStat
                n={`${summary.users_with_2sv_enrolled ?? 0} / ${summary.users_with_2sv_enforced ?? 0}`}
                l="2-Step Verification"
                sub="enrolled / enforced"
                onClick={() => applyFilter('enforced_2sv')}
                active={userFilter === 'enforced_2sv'}
                testId="gw-stat-2sv"
              />
            </div>
          </div>

          {/* Row 2 — Structural inventory */}
          <div className="pt-4 border-t border-slate-100">
            <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-3 font-medium">Workspace inventory</p>
            <div className="flex flex-wrap items-start gap-x-8 gap-y-4">
              <GwStat n={summary.groups_total ?? groups.length} l="Groups" />
              <GwStat
                n={summary.distribution_lists_total ?? groups.filter((g) => ((g.group_type || '') + '').toLowerCase().startsWith('distribution')).length}
                l="Distribution Lists"
                sub="estimated"
                tone="blue"
              />
              <GwStat n={summary.org_units_total ?? orgUnits.length} l="Org Units" />
              <GwStat n={summary.domains_total ?? domains.length} l="Domains" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList>
          <TabsTrigger value="users" data-testid="gw-tab-users">Users ({users.length})</TabsTrigger>
          <TabsTrigger value="groups" data-testid="gw-tab-groups">Groups ({groups.length})</TabsTrigger>
          <TabsTrigger value="ous" data-testid="gw-tab-ous">Org Units ({orgUnits.length})</TabsTrigger>
          <TabsTrigger value="roles" data-testid="gw-tab-roles">Roles ({roles.length})</TabsTrigger>
          <TabsTrigger value="domains" data-testid="gw-tab-domains">Domains ({domains.length})</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card>
        <CardContent className="p-0">
          {tab === 'users' && (
            <>
              {userFilter && (
                <div
                  className="flex items-center justify-between gap-3 px-4 py-2.5 border-b bg-navy-50/40"
                  data-testid="gw-active-filter-bar"
                >
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-slate-600">Filter:</span>
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full bg-navy-100 text-navy-800 px-2.5 py-0.5 text-xs font-medium"
                      data-testid="gw-active-filter-chip"
                    >
                      {filterLabels[userFilter]}
                      <button
                        type="button"
                        onClick={() => setUserFilter(null)}
                        className="hover:bg-navy-200 rounded-full w-4 h-4 flex items-center justify-center text-navy-700"
                        aria-label="Remove filter"
                        data-testid="gw-clear-filter-x"
                      >
                        ×
                      </button>
                    </span>
                    <span className="text-xs text-slate-500">
                      Showing {filteredUsers.length} of {users.length}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setUserFilter(null)}
                    className="text-xs font-medium text-navy-700 hover:underline"
                    data-testid="gw-clear-filter-btn"
                  >
                    Clear filter
                  </button>
                </div>
              )}
              <table className="min-w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr className="text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="text-left px-4 py-2 font-medium">Name</th>
                  <th className="text-left px-4 py-2 font-medium">Email</th>
                  <th className="text-left px-4 py-2 font-medium">Org Unit</th>
                  <th className="text-left px-4 py-2 font-medium">Status</th>
                  <th className="text-left px-4 py-2 font-medium">Admin</th>
                  <th className="text-left px-4 py-2 font-medium">2-Step Verification</th>
                  <th className="text-left px-4 py-2 font-medium">Last login</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => {
                  const isAdmin = u.isAdmin || u.isDelegatedAdmin;
                  const status = u.suspended ? 'Suspended' : u.archived ? 'Archived' : 'Active';
                  const statusColor = status === 'Active' ? 'text-emerald-700' : status === 'Suspended' ? 'text-red-700' : 'text-slate-500';
                  const twoSv = u.isEnforcedIn2Sv ? { label: 'Enforced', color: 'text-emerald-700' }
                    : u.isEnrolledIn2Sv ? { label: 'Enrolled', color: 'text-slate-700' }
                    : { label: 'Not enrolled', color: 'text-red-700' };
                  return (
                    <tr key={u.id} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="px-4 py-2 text-slate-900">{displayName(u)}</td>
                      <td className="px-4 py-2 font-mono text-xs text-slate-600">{u.primaryEmail}</td>
                      <td className="px-4 py-2 text-xs text-slate-500">{u.orgUnitPath || '/'}</td>
                      <td className={`px-4 py-2 text-xs font-medium ${statusColor}`}>{status}</td>
                      <td className="px-4 py-2 text-xs text-slate-600">{isAdmin ? 'Admin' : <span className="text-slate-300">—</span>}</td>
                      <td className={`px-4 py-2 text-xs font-medium ${twoSv.color}`}>{twoSv.label}</td>
                      <td className="px-4 py-2 text-xs text-slate-500">{fmt(u.lastLoginTime)}</td>
                    </tr>
                  );
                })}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-400" data-testid="gw-users-empty">
                      No users match this filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </>
          )}

          {tab === 'groups' && (
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr className="text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="text-left px-4 py-2 font-medium">Name</th>
                  <th className="text-left px-4 py-2 font-medium">Email</th>
                  <th className="text-left px-4 py-2 font-medium">Type</th>
                  <th className="text-left px-4 py-2 font-medium">Members</th>
                  <th className="text-left px-4 py-2 font-medium">Sample</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => {
                  const gType = g.group_type || (g.email ? 'Distribution List' : 'Group');
                  const isDL = gType.toLowerCase().startsWith('distribution');
                  const typeColor = isDL ? 'text-blue-700' : gType === 'Security Group' ? 'text-purple-700' : 'text-slate-700';
                  return (
                    <tr key={g.id} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="px-4 py-2 text-slate-900">{g.name}</td>
                      <td className="px-4 py-2 font-mono text-xs text-slate-600">{g.email}</td>
                      <td className={`px-4 py-2 text-xs font-medium ${typeColor}`}>{gType}</td>
                      <td className="px-4 py-2 text-slate-700">{g.directMembersCount}</td>
                      <td className="px-4 py-2 text-xs text-slate-500">
                        {(groupMembers[g.id] || []).slice(0, 3).map((m) => m.email).join(', ') || '—'}
                        {(groupMembers[g.id] || []).length > 3 && (
                          <span className="text-slate-400"> +{(groupMembers[g.id] || []).length - 3} more</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {tab === 'ous' && (
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr className="text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="text-left px-4 py-2 font-medium">Name</th>
                  <th className="text-left px-4 py-2 font-medium">Path</th>
                  <th className="text-left px-4 py-2 font-medium">Parent</th>
                </tr>
              </thead>
              <tbody>
                {orgUnits.map((o) => (
                  <tr key={o.orgUnitId} className="border-b last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-2 text-slate-900">{o.name}</td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-600">{o.orgUnitPath}</td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-500">{o.parentOrgUnitPath}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {tab === 'roles' && (
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr className="text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="text-left px-4 py-2 font-medium">Role</th>
                  <th className="text-left px-4 py-2 font-medium">System</th>
                  <th className="text-left px-4 py-2 font-medium">Super Admin</th>
                  <th className="text-left px-4 py-2 font-medium">Assignees</th>
                </tr>
              </thead>
              <tbody>
                {roles.map((r) => {
                  const assignees = roleAssignments.filter((a) => String(a.roleId) === String(r.roleId));
                  return (
                    <tr key={r.roleId} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="px-4 py-2 text-slate-900">{r.roleName}</td>
                      <td className="px-4 py-2 text-xs text-slate-600">{r.isSystemRole ? 'Yes' : 'No'}</td>
                      <td className="px-4 py-2 text-xs text-slate-600">{r.isSuperAdminRole ? 'Yes' : <span className="text-slate-300">—</span>}</td>
                      <td className="px-4 py-2 text-xs text-slate-500">
                        {assignees.length === 0 ? '—' : assignees.map((a) => a.assignedTo).join(', ')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {tab === 'domains' && (
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr className="text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="text-left px-4 py-2 font-medium">Domain</th>
                  <th className="text-left px-4 py-2 font-medium">Primary</th>
                  <th className="text-left px-4 py-2 font-medium">Verified</th>
                  <th className="text-left px-4 py-2 font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {domains.map((d) => (
                  <tr key={d.domainName} className="border-b last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-2 font-mono text-xs text-slate-700">{d.domainName}</td>
                    <td className="px-4 py-2 text-xs text-slate-600">{d.isPrimary ? 'Yes' : <span className="text-slate-300">—</span>}</td>
                    <td className={`px-4 py-2 text-xs font-medium ${d.verified ? 'text-emerald-700' : 'text-red-700'}`}>{d.verified ? 'Verified' : 'Unverified'}</td>
                    <td className="px-4 py-2 text-xs text-slate-500">{fmt(d.creationTime)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </>
  );
};

const GwStat = ({ n, l, sub, tone = 'slate', onClick, active = false, testId }) => {
  const color = tone === 'emerald' ? 'text-emerald-700'
    : tone === 'red' ? 'text-red-700'
    : tone === 'blue' ? 'text-blue-700'
    : 'text-slate-900';
  const isClickable = typeof onClick === 'function';
  const interactive = isClickable
    ? `group cursor-pointer rounded-md -mx-2 px-2 py-1.5 transition-all ${active
        ? 'bg-navy-50 ring-1 ring-navy-300 shadow-sm'
        : 'hover:bg-slate-100 hover:ring-1 hover:ring-slate-200 active:scale-[0.98]'}`
    : '';
  const Tag = isClickable ? 'button' : 'div';
  return (
    <Tag
      type={isClickable ? 'button' : undefined}
      onClick={onClick}
      data-testid={testId}
      title={isClickable ? `Filter Users by: ${l}` : undefined}
      className={`min-w-[64px] text-left ${interactive}`}
    >
      <div className="flex items-baseline gap-1.5">
        <p className={`text-xl font-semibold leading-none ${color}`}>{n}</p>
        {isClickable && (
          <span
            aria-hidden
            className={`text-[10px] leading-none ${active ? 'text-navy-600' : 'text-slate-300 group-hover:text-slate-500'} transition-colors`}
          >
            ▸
          </span>
        )}
      </div>
      <p className={`text-[11px] mt-1.5 uppercase tracking-wide whitespace-nowrap ${active ? 'text-navy-700 font-semibold' : 'text-slate-500'}`}>{l}</p>
      {sub && (
        <p className="text-[10px] text-slate-400 mt-0.5 normal-case tracking-normal italic">{sub}</p>
      )}
    </Tag>
  );
};

export default EvidenceDetails;
